import { useState } from 'react'
import { api } from '@/api'
import { formatDate } from '@/utils'

export default function OrderSearch() {
  const [searchType, setSearchType] = useState<'phone' | 'name'>('phone')
  const [searchValue, setSearchValue] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [searched, setSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  /**
   * 查询订单方法
   * 支持两种查询方式：
   * 1. 按手机号查询 - 精确匹配
   * 2. 按姓名查询 - 模糊匹配
   * 
   * 使用方式：
   * const orders = await searchOrders('13800138000', 'phone')  // 按手机号
   * const orders = await searchOrders('张三', 'name')          // 按姓名
   */
  const searchOrders = async (value: string, type: 'phone' | 'name') => {
    if (!value.trim()) {
      setError('请输入查询内容')
      return []
    }

    setLoading(true)
    setError('')
    
    try {
      // 获取所有预约数据
      const result = await api.appointments.getAll()
      const allAppointments = result.data || []

      // 根据查询类型过滤
      let filtered: any[] = []
      
      if (type === 'phone') {
        // 手机号精确匹配
        filtered = allAppointments.filter(apt => 
          apt.customer_phone === value.trim()
        )
      } else {
        // 姓名模糊匹配（包含即可）
        filtered = allAppointments.filter(apt => 
          apt.customer_name.includes(value.trim())
        )
      }

      // 按时间倒序排序
      filtered.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date} ${a.appointment_time}`)
        const dateB = new Date(`${b.appointment_date} ${b.appointment_time}`)
        return dateB.getTime() - dateA.getTime()
      })

      setResults(filtered)
      setSearched(true)
      return filtered
    } catch (err: any) {
      setError('查询失败：' + err.message)
      return []
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    searchOrders(searchValue, searchType)
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800',
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: '待确认',
      confirmed: '已确认',
      completed: '已完成',
      cancelled: '已取消',
      no_show: '未到场',
    }
    return labels[status] || status
  }

  return (
    <div className="page-container pb-20">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">订单查询</h1>

      {/* 查询表单 */}
      <form onSubmit={handleSubmit} className="card mb-6">
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSearchType('phone')}
            className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
              searchType === 'phone'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            手机号查询
          </button>
          <button
            type="button"
            onClick={() => setSearchType('name')}
            className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
              searchType === 'name'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            姓名查询
          </button>
        </div>

        <div className="flex gap-2">
          <input
            type={searchType === 'phone' ? 'tel' : 'text'}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder={searchType === 'phone' ? '请输入手机号' : '请输入姓名'}
            className="input flex-1"
          />
          <button
            type="submit"
            disabled={loading || !searchValue.trim()}
            className="btn-primary py-3 px-6 disabled:opacity-50"
          >
            {loading ? '查询中...' : '查询'}
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-600 mt-2">{error}</p>
        )}
      </form>

      {/* 查询结果 */}
      {searched && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            查询结果 {results.length > 0 && `(${results.length}条)`}
          </h2>

          {results.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>未找到相关订单</p>
              <p className="text-sm mt-2">
                {searchType === 'phone' ? '请检查手机号是否正确' : '请检查姓名是否正确'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map(apt => (
                <div key={apt.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{apt.customer_name}</h3>
                      <p className="text-sm text-gray-500">{apt.customer_phone}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(apt.status)}`}>
                      {getStatusLabel(apt.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-gray-500">服务：</span>
                      <span className="text-gray-900">{apt.service?.name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">美容师：</span>
                      <span className="text-gray-900">{apt.beautician?.name || '未指定'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">日期：</span>
                      <span className="text-gray-900">{formatDate(apt.appointment_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">时间：</span>
                      <span className="text-gray-900">{apt.appointment_time}</span>
                    </div>
                  </div>

                  {apt.notes && (
                    <p className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                      备注：{apt.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
